import { Languages } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useSession } from '@/features/auth/use-session'
import { api } from '@/shared/api/client'
import type { UserRead } from '@/shared/api/contracts'
import { errorMessage } from '@/shared/components/data'
import { Select } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import { LOCALES, type Locale } from '@/shared/i18n/locale'
import { qk } from '@/shared/api/query-keys'
import { cn } from '@/shared/lib/cn'

/** Cada idioma en su propio idioma: `Português`, no "Portugués". */
const NOMBRE: Record<Locale, string> = {
  es: 'Español',
  pt: 'Português',
  en: 'English',
}

/**
 * Elegir el idioma **propio**.
 *
 * `docs/i18n.md` dice que el idioma es de la persona, no de la aplicación, y
 * que "cambiarlo en el perfil invalida `qk.auth.me()`". El perfil no existía:
 * el único selector de idioma de toda la aplicación estaba en
 * `/settings/users`, que es **admin-only**, y servía para cambiarle el idioma
 * **a otro**. Un `tech_member` que quisiera trabajar en inglés tenía que
 * pedírselo a un admin. El QA funcional lo levantó como 5.2.
 *
 * Guarda con `PATCH /users/{id}` sobre uno mismo, que el backend ya permite
 * ("uno mismo siempre; otro, solo si sos admin"), e invalida la sesión: el
 * idioma sale de `GET /auth/me`, así que hasta que esa query no se refresque la
 * pantalla sigue en el idioma viejo.
 *
 * **No está en el portal**, y no es un olvido: el cliente es read-only absoluto
 * y `/api/v1/portal` sólo registra GET — hay un test de contrato que lo exige.
 * El idioma de un contacto lo corrige el equipo desde su ficha.
 */
export function LocalePicker({ className }: { className?: string }) {
  const t = useT()
  const { me } = useSession()
  const client = useQueryClient()

  const guardar = useMutation({
    mutationFn: (locale: Locale) => api.patch<UserRead>(`users/${me!.id}`, { locale }),
    onSuccess: () => {
      // El idioma vive en `/auth/me`: sin invalidar, la pantalla se queda en el
      // anterior hasta la próxima recarga.
      void client.invalidateQueries({ queryKey: qk.auth.me() })
    },
    onError: (error) => toast.error(errorMessage(error, t('appearance.locale.error'))),
  })

  if (!me) return null

  return (
    <label
      className={cn('inline-flex items-center gap-1.5 text-text-2', className)}
      title={t('appearance.locale.label')}
    >
      <Languages size={14} aria-hidden="true" className="shrink-0" />
      <span className="sr-only">{t('appearance.locale.label')}</span>
      <Select
        value={me.locale}
        disabled={guardar.isPending}
        onChange={(event) => guardar.mutate(event.target.value as Locale)}
        className="h-7 w-auto min-w-[104px] border-0 bg-transparent px-1 text-xs"
      >
        {LOCALES.map((locale) => (
          <option key={locale} value={locale}>
            {NOMBRE[locale]}
          </option>
        ))}
      </Select>
    </label>
  )
}
